import React, { useCallback, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Alert, Button, Stack } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { Dayjs } from '~/services/TimeService';

interface Props {
  onAdd: (start: string, end: string) => void;
}

export default function AddDateSection({ onAdd }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    setStartDate(null);
    setEndDate(null);
    setDateError(null);
  }, []);

  const handleAdd = useCallback(() => {
    if (!startDate || !endDate || !startDate.isValid() || !endDate.isValid()) return;
    if (endDate.isBefore(startDate, 'day')) {
      setDateError('End date must be on or after start date');
      return;
    }
    onAdd(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'));
    setIsOpen(false);
    setStartDate(null);
    setEndDate(null);
    setDateError(null);
  }, [startDate, endDate, onAdd]);

  if (!isOpen) {
    return (
      <Button size="small" variant="text" startIcon={<AddIcon />} onClick={handleOpen} sx={{ alignSelf: 'flex-start' }}>
        Add Date
      </Button>
    );
  }

  return (
    <Stack gap={0.5}>
      <Stack direction="row" gap={1} alignItems="center">
        <DatePicker
          label="From"
          value={startDate}
          onChange={(val) => {
            setStartDate(val);
            setDateError(null);
          }}
          slotProps={{ textField: { size: 'small' } }}
        />
        <DatePicker
          label="To"
          value={endDate}
          onChange={(val) => {
            setEndDate(val);
            setDateError(null);
          }}
          slotProps={{ textField: { size: 'small' } }}
        />
        <Button variant="contained" size="small" disabled={!startDate || !endDate} onClick={handleAdd}>
          Add
        </Button>
        <Button variant="text" size="small" onClick={handleCancel}>
          Cancel
        </Button>
      </Stack>
      {dateError && (
        <Alert severity="error" sx={{ py: 0 }}>
          {dateError}
        </Alert>
      )}
    </Stack>
  );
}
